/**
 * React hook that polls the edge failover brain (GET /api/sync/target) every 10s (WCO-3793).
 * Used by SyncStatusIndicator to warn supervisors/managers when work done during an internet outage is not
 * confirmed on the cloud. Returns null when the sync-service is not deployed/reachable (cloud deployments) or when
 * failover is disabled on this edge.
 */
import { useState, useEffect, useCallback } from 'react';
import syncService, { FailoverStatus } from '../services/syncService';

const POLL_INTERVAL_MS = 10_000;

export interface UseFailoverStatusResult {
  /** Failover state, null until the first successful fetch or when unavailable/disabled */
  failover: FailoverStatus | null;
}

export function useFailoverStatus(): UseFailoverStatusResult {
  const [failover, setFailover] = useState<FailoverStatus | null>(null);

  const fetchFailover = useCallback(async () => {
    try {
      const data = await syncService.getFailover();
      setFailover(data?.enabled ? data : null);
    } catch {
      // Sync service not running / not reachable (e.g. cloud deployments) — nothing to show
      setFailover(null);
    }
  }, []);

  useEffect(() => {
    fetchFailover();
    const id = setInterval(fetchFailover, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchFailover]);

  return { failover };
}
